import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import { Dropdown, Modal } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { AppState } from '@nxsys/store';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { notificationActions } from '@nxsys/store/slices/notification/reducer/notificationSlice';
import { messageActions } from '@nxsys/store/slices/message/reducer/messageSlice';
import { roles } from '@nxsys/store/constants';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { showMessage } from '@nxsys/utils/message';
import { StyledCountryBtn, StyledCountryItem } from './index.styled';
import type { AppDispatch } from '@nxsys/store';
import type { IUserRoles } from '@nxsys/types/models/User';

const COUNTRY_MAP: Record<string, { label: string; code: string }> = {
  [UNITED_KINGDOM]: { label: 'United Kingdom', code: 'GB' },
  [IRELAND]: { label: 'Ireland', code: 'IE' },
};

// Mirrors users.service.ts#switchUserByCountry's own rejection list — the
// backend 400s POST /auth/login/country for these raw roles regardless of
// what the FE sends, so a matching user must never even see the switcher.
const COUNTRY_SWITCH_REJECTED_ROLES: IUserRoles[] = [
  'super_admin',
  'agency',
  'subcontractor',
];

const AppPayrollCountrySwitcher: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const { user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );

  // The render sites (AppHeader in both AppLayout variants) gate on the
  // display `role` string (hiding SUPPER_ADMIN/EMPLOYEE) — not the raw
  // `roles` enum the backend actually checks, and 'agency' isn't covered by
  // that gate at all. So an agency user reaches this component with a live,
  // enabled switcher that 400s on click. Recheck the backend's own rejection
  // list directly against `roles` here rather than relying on the render
  // site to stay in sync with it (NX-5933).
  if (!user || COUNTRY_SWITCH_REJECTED_ROLES.includes(user.roles)) {
    return null;
  }

  const isSuperAdmin = user.isSuperAdmin ?? false;

  const doSwitch = async (country: string) => {
    const params = {
      username: user.username,
      payrollCountry: country,
      userType:
        user.role === roles.employer && user.isAdminCompanyUser
          ? 'employer'
          : 'bureau',
      isSuperAdmin,
    };

    try {
      dispatch(appActions.onChangeLoading({ loading: true }));
      const resp = await dispatch(appActions.switchCountry(params));

      if (resp.meta.requestStatus === 'rejected') {
        dispatch(appActions.onChangeLoading({ loading: false }));
        showMessage({
          type: 'error',
          content:
            (resp.payload as any)?.response?.data?.message ||
            'Failed to switch country',
        });
        return;
      }

      showMessage({
        type: 'success',
        content: `Payroll country switched to ${COUNTRY_MAP[country]?.label}`,
      });

      // switchCountry has already flipped payrollCountry, so the sidebar is
      // rendering the new country's routes from here on. Permissions are
      // country-filtered server-side (isUK / isIreland), so they must be
      // refetched too — in parallel, to keep that window as short as possible,
      // and unwrapped so a failed refetch surfaces instead of silently leaving
      // the previous country's list in place. NX-5840.
      const [me] = await Promise.all([
        dispatch(appActions.getMe()).unwrap(),
        dispatch(appActions.getAppConfigs()),
        dispatch(appActions.getPermissions()).unwrap(),
      ]);
      // NX-6278: the bell and unread count still held the old country's.
      const newUser = me?.data?.data;
      dispatch(
        notificationActions.getNotification({
          role: newUser?.role,
          userId: newUser?.id,
          subcontractorId: newUser?.subcontractorId?.id,
          agencyId: newUser?.agencyId?.id,
        }),
      );
      dispatch(messageActions.fetchUnreadCount());
      dispatch(appActions.onChangeLoading({ loading: false }));
      navigate('/dashboards');
    } catch (err) {
      console.error(err);
      showMessage({ type: 'error', content: 'Something went wrong' });
      dispatch(appActions.onChangeLoading({ loading: false }));
    }
  };

  // We have no cheap "does the child identity already exist" signal on the FE
  // yet (that needs a BE-exposed field, out of this phase's scope), so the
  // confirmation is unconditional rather than only-on-first-switch: it is a
  // no-op cost when the workspace already exists, and a real warning when it
  // doesn't. Revisit once `/me` exposes the parent's linked countries.
  const handlePayrollCountryUpdate = (country: string) => {
    if (country === payrollCountry) return;
    const targetLabel = COUNTRY_MAP[country]?.label || country;
    Modal.confirm({
      title: `Switch to ${targetLabel}?`,
      content:
        'If you do not already have a payroll workspace for this country, switching will create one for your account. This cannot be undone.',
      okText: 'Switch',
      cancelText: 'Cancel',
      onOk: () => doSwitch(country),
    });
  };

  const items = Object.entries(COUNTRY_MAP).map(([key, { label, code }]) => ({
    key,
    disabled: key === payrollCountry,
    label: (
      <StyledCountryItem onClick={() => handlePayrollCountryUpdate(key)}>
        <ReactCountryFlag countryCode={code} svg />
        <h4>{label}</h4>
      </StyledCountryItem>
    ),
  }));

  const currentCountry =
    COUNTRY_MAP[payrollCountry] || COUNTRY_MAP[UNITED_KINGDOM];

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      overlayStyle={{ zIndex: 1052 }}
      disabled={user.isAdminCompany}
    >
      <StyledCountryBtn
        className="ant-dropdown-link"
        onClick={(e) => e.preventDefault()}
      >
        <ReactCountryFlag
          countryCode={currentCountry.code}
          svg
          style={{ width: '1.2em', height: '1.2em' }}
        />
      </StyledCountryBtn>
    </Dropdown>
  );
};

export default AppPayrollCountrySwitcher;
